import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, ExternalLink } from 'lucide-react';
import { getLocationSettings, getUniversalCatalog } from '../services/api';

const Footer = () => {
  const [location, setLocation] = useState({
    address_line1: 'K-237,238,PHASE VIII FOCAL POINT',
    address_line2: 'LUDHIANA, PUNJAB 141010(INDIA)',
    phone: '+91 78883 32031',
    email: 'sales@namohtoolings.in',
    map_embed_url: '',
    google_maps_link: ''
  });
  const [catalogUrl, setCatalogUrl] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, catRes] = await Promise.all([
          getLocationSettings().catch(() => ({ data: {} })),
          getUniversalCatalog().catch(() => ({ data: { catalog_url: null } }))
        ]);
        if (locRes.data && Object.keys(locRes.data).length > 0) setLocation(prev => ({ ...prev, ...locRes.data }));
        if (catRes.data?.catalog_url) setCatalogUrl(catRes.data.catalog_url);
      } catch { /* use defaults */ }
    };
    fetchData();
  }, []);

  const getCatalogFullUrl = () => {
    if (!catalogUrl) return null;
    return catalogUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${catalogUrl}` : catalogUrl;
  };

  return (
    <footer className="bg-gradient-to-br from-[#1a1f3a] via-[#2E3A6F] to-[#1a1f3a] text-white">
      {/* Map Section */}
      {location.map_embed_url && (
        <div className="w-full h-64 md:h-80" data-testid="footer-map">
          <iframe
            src={location.map_embed_url}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Namoh Toolings Location"
          />
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Namoh Toolings</h3>
            <p className="text-white/70 text-sm leading-relaxed">
              Premium Cutting Tools & Tooling Solutions. Your trusted partner for precision machining tools across India.
            </p>
            <p className="text-white/50 text-xs italic">We Listen, We Innovate</p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/90">Quick Links</h4>
            <nav className="flex flex-col gap-2">
              {[
                { to: '/products', label: 'Our Products' },
                { to: '/about', label: 'About Us' },
                { to: '/calculator', label: 'Cutting Calculator' },
                { to: '/events', label: 'Events' },
                { to: '/enquiry', label: 'Contact Us' },
                { to: '/dealership', label: 'Dealership' },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="text-white/60 hover:text-white text-sm flex items-center gap-1 transition-colors">
                  <ArrowRight size={12} /> {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Products */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/90">Products</h4>
            <nav className="flex flex-col gap-2">
              {['Turning Tools', 'Milling Tools', 'Drilling Tools', 'Grooving Tools', 'Parting Tools', 'VCI Packaging'].map((p) => (
                <Link key={p} to="/products" className="text-white/60 hover:text-white text-sm flex items-center gap-1 transition-colors">
                  <ArrowRight size={12} /> {p}
                </Link>
              ))}
            </nav>
            {getCatalogFullUrl() ? (
              <a href={getCatalogFullUrl()} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors" data-testid="footer-catalog-btn">
                <ExternalLink size={14} /> Download Catalog
              </a>
            ) : (
              <span className="mt-2 inline-flex items-center gap-2 text-sm bg-white/5 px-4 py-2 rounded-lg text-white/40 cursor-not-allowed" data-testid="footer-catalog-btn">
                <ExternalLink size={14} /> Catalog Coming Soon
              </span>
            )}
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/90">Contact Us</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-white/50 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-white/70">
                  <p>{location.address_line1}</p>
                  <p>{location.address_line2}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-white/50 flex-shrink-0" />
                <a href={`tel:${location.phone}`} className="text-sm text-white/70 hover:text-white transition-colors">{location.phone}</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-white/50 flex-shrink-0" />
                <a href={`mailto:${location.email}`} className="text-sm text-white/70 hover:text-white transition-colors">{location.email}</a>
              </div>
              {location.google_maps_link && (
                <a href={location.google_maps_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mt-2">
                  <MapPin size={14} /> Get Directions <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-white/50">
          <p>&copy; {new Date().getFullYear()} Namoh Toolings. All rights reserved.</p>
          <p>Precision Cutting Tools & Tooling Solutions</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
